import { postDataBootstrap } from "@fmc-digital/auth-api";
import { getOAuthRequestParams, getRedirectUrlFromSearch } from "./oauth.utils";
import { config } from "@/config";

/**
 * ACP Data Warm-up: Pre-fetch customer data for Authenticated Customer Portal
 * 
 * This function is called before authentication completes to warm up
 * backend caches for ACP (Authenticated Customer Portal) users. This reduces
 * the perceived load time after login by pre-fetching data while the
 * user is completing authentication.
 * 
 * Only runs when:
 * - User has entered a valid username
 * - Request originated from ACP (detected via redirect URL to myfreedom domains)
 * 
 * @param username - The username entered by the user
 */
export async function warmupACPData(username: string): Promise<void> {
  if (!username?.trim()) {
    return;
  }

  // Check if this is an ACP request by examining the redirect URL
  if (!isACPRequest()) {
    // Not an ACP flow, skip data bootstrap
    return;
  }

  try {
    console.debug('[ACP Bootstrap] Warming up data for ACP user:', username);
    
    // Call data bootstrap API for ACP data warm-up
    // This is a fire-and-forget call - we don't wait for the response
    await postDataBootstrap({
      userName: username,
      refresh: true,
      settings: [],
    });
    
    console.debug('[ACP Bootstrap] Data warm-up initiated successfully');
  } catch (error) {
    // Silent failure - this is an optimization, not critical
    console.warn('[ACP Bootstrap] Data warm-up failed (non-critical):', error);
  }
}

/**
 * Check if the current request is from ACP (Authenticated Customer Portal)
 * 
 * ACP is identified by redirect URLs pointing to myfreedom domains:
 * - myfreedom.freedommortgage.com (production)
 * - myfreedomuat.freedommortgage.com (UAT)
 * - myfreedomtest.freedommortgage.com (test/dev)
 * 
 * @returns true if this is an ACP request
 */
function isACPRequest(): boolean {
  // Check OAuth parameters for redirect URL
  const oauthParams = getOAuthRequestParams();
  const searchParams = new URLSearchParams(oauthParams);
  const redirectUri = searchParams.get("redirect_uri");
  
  if (redirectUri && isACPDomain(redirectUri)) {
    return true;
  }

  // Check for redirect_url in search params
  const redirectUrl = getRedirectUrlFromSearch(oauthParams);
  if (redirectUrl && isACPDomain(redirectUrl)) {
    return true;
  }

  // Check dashboardRedirectUri from config
  const dashboardUri = config.env("dashboardRedirectUri");
  if (dashboardUri && isACPDomain(dashboardUri)) {
    return true;
  }

  return false;
}

/**
 * Check if a URL points to an ACP (myfreedom) domain
 * 
 * @param url - URL to check
 * @returns true if URL contains myfreedom domain
 */
function isACPDomain(url: string): boolean {
  if (!url) {
    return false;
  }

  try {
    const urlLower = url.toLowerCase();
    
    // Check for myfreedom domains (ACP)
    const acpDomains = [
      'localhost:8080',
      'myfreedom.freedommortgage.com',      // Production
      'myfreedomuat.freedommortgage.com',   // UAT
      'myfreedomtest.freedommortgage.com',  // Test
      'myfreedomdev.freedommortgage.com',  // Dev
    ];

    return acpDomains.some(domain => urlLower.includes(domain));
  } catch (error) {
    console.warn('[ACP Bootstrap] Invalid URL format:', url, error);
    return false;
  }
}
